// RideShareNewRideLocationPicker.js
import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";
import Modal from "react-modal";

const libraries = ["places"];

const RideShareDriverRouteLocationPicker = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const [locations, setLocations] = useState({
    origin: { coords: null, address: "", name: "" },
    waypoint: { coords: null, address: "", name: "" },
    destination: { coords: null, address: "", name: "" },
  });

  const [userLocation, setUserLocation] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState("");

  const modalMapRef = useRef(null);
  const [modalMarker, setModalMarker] = useState(null);
  const autocompleteRef = useRef(null);

  const reverseGeocode = async (lat, lng, apiKey) => {
    try {
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
      const response = await fetch(geocodeUrl);
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          return data.results[0].formatted_address;
        }
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  // Get user's current location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          console.log("User location obtained:", { lat: latitude, lng: longitude });
        },
        (error) => {
          console.error("Error getting user location:", error);
          // Fallback to default location if geolocation fails
          setUserLocation({ lat: -17.8273732, lng: 31.0435011 });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      console.log("Geolocation is not supported by this browser");
      // Fallback to default location
      setUserLocation({ lat: -17.8273732, lng: 31.0435011 });
    }
  }, []);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;
    const coords = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setModalMarker(coords);
  };

  const confirmLocation = async () => {
    if (!activeModal || !modalMarker) return;
    const place = autocompleteRef.current?.getPlace();
    let formattedAddress = place?.formatted_address || locations[activeModal].address;
    
    // If no address from autocomplete, use reverse geocoding
    if (!formattedAddress || formattedAddress === "") {
      formattedAddress = await reverseGeocode(
        modalMarker.lat, 
        modalMarker.lng, 
        getGoogleMapsApiKey()
      );
    }
    
    setLocations((prev) => ({
      ...prev,
      [activeModal]: {
        coords: modalMarker,
        address: formattedAddress,
        name: formattedAddress,
      },
    }));
    setActiveModal(null);
    setModalMarker(null);
  };

  useEffect(() => {
    const { origin, waypoint, destination } = locations;
    if (!origin.coords || !destination.coords) return;

    const waypts = waypoint.coords ? [{ location: waypoint.coords, stopover: true }] : [];

    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: origin.coords,
        destination: destination.coords,
        waypoints: waypts,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          const totalDuration = result.routes[0].legs.reduce((sum, leg) => sum + leg.duration.value, 0);
          setEta(`${Math.ceil(totalDuration / 60)} min`);
        } else {
          console.error("Directions request failed:", status);
        }
      }
    );
  }, [locations]);

  if (!isLoaded) return <div className="text-center mt-10">Loading map...</div>;

  const mapCenter = userLocation || locations.origin.coords || { lat: -17.8273732, lng: 31.0435011 };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-6 space-y-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">New Ride Location Picker</h1>
        
        <h5 className="text-2  text-green-800 mb-6">Please Create your route</h5>
        

        {["origin", "waypoint", "destination"].map((loc) => {
          // User-friendly labels - shortened
          const labels = {
            origin: "Starting",
            waypoint: "Passing Through",
            destination: "Ending"
          };
          
          return (
            <div
              key={loc}
              className="bg-gray-100 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-700 text-sm">
                  {labels[loc]}
                </span>
                <button
                  onClick={() => setActiveModal(loc)}
                  className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  {locations[loc].coords ? "Change" : "Select"}
                </button>
              </div>
              <div className="text-xs text-gray-500 break-words">
                {locations[loc].address || locations[loc].name || "Not selected"}
              </div>
            </div>
          );
        })}
         <div className="text-lg font-semibold text-gray-800">ETA: {eta || "N/A"}</div>
        </div>
        <div className="rounded-lg overflow-hidden shadow-md" style={{ height: "400px" }}>
          <GoogleMap center={mapCenter} zoom={12} mapContainerStyle={{ width: "100%", height: "100%" }}>
            {userLocation && (
              <Marker 
                position={userLocation} 
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 20,
                  fillColor: "#4285F4",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 1,
                }}
                label="You"
              />
            )}
            {locations.origin.coords && <Marker position={locations.origin.coords} label="O" />}
            {locations.waypoint.coords && <Marker position={locations.waypoint.coords} label="W" />}
            {locations.destination.coords && <Marker position={locations.destination.coords} label="D" />}
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  polylineOptions: { strokeColor: "#800080", strokeWeight: 5 }, // Purple route line
                  suppressMarkers: true,
                }}
              />
            )}
          </GoogleMap>
        </div>

        <div className="flex flex-col space-y-2">
          <button
            onClick={() => {
              if (userLocation) {
                // Center map on user location
                const mapElement = document.querySelector('iframe');
                if (mapElement) {
                  // This will trigger a re-render with user location as center
                  setUserLocation({ ...userLocation });
                }
              } else {
                // Try to get location again
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords;
                      setUserLocation({ lat: latitude, lng: longitude });
                      console.log("User location obtained:", { lat: latitude, lng: longitude });
                    },
                    (error) => {
                      console.error("Error getting user location:", error);
                      alert("Unable to get your location. Please enable location services.");
                    }
                  );
                }
              }
            }}
            className="w-full px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors my-4"
          >
            📍 Center on My Location
          </button>

 

        <button
          onClick={async () => {
            const updatedLocations = { ...locations };
            
            // Ensure all selected locations have proper names via reverse geocoding
            for (const key of ['origin', 'destination']) {
              if (updatedLocations[key].coords && (!updatedLocations[key].name || updatedLocations[key].name === "")) {
                const address = await reverseGeocode(
                  updatedLocations[key].coords.lat,
                  updatedLocations[key].coords.lng,
                  getGoogleMapsApiKey()
                );
                updatedLocations[key].name = address;
                updatedLocations[key].address = address;
              }
            }
            
            // Handle waypoint if it exists
            if (updatedLocations.waypoint.coords && (!updatedLocations.waypoint.name || updatedLocations.waypoint.name === "")) {
              const address = await reverseGeocode(
                updatedLocations.waypoint.coords.lat,
                updatedLocations.waypoint.coords.lng,
                getGoogleMapsApiKey()
              );
              updatedLocations.waypoint.name = address;
              updatedLocations.waypoint.address = address;
            }
            
            window.ReactNativeWebView.postMessage(JSON.stringify({ locations: updatedLocations, eta }));
            console.log("🗺️ WebView - Sending locations to React Native:", updatedLocations);
          }}
          className="w-full py-3 bg-[#ffc000] rounded-lg text-black font-bold hover:bg-[#e3ab00] transition-colors"
        >
          Continue
        </button>
      </div>

      {/* Modal */}
      <Modal
        isOpen={!!activeModal}
        onRequestClose={() => setActiveModal(null)}
        className="bg-white rounded-xl max-w-3xl w-[95%] mx-auto p-6 shadow-lg outline-none"
        style={{ maxHeight: 'calc(100vh - 100px)', height: 'auto' }}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        <h2 className="text-lg font-semibold mb-2">
          {activeModal === "origin" && "Select Starting Point"}
          {activeModal === "waypoint" && "Select Stop"}
          {activeModal === "destination" && "Select Ending Point"}
        </h2>
        <Autocomplete
          onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
          onPlaceChanged={handlePlaceSelect}
        >
          <input
            type="text"
            placeholder={`Search ${activeModal}`}
            className="w-full border border-gray-300 rounded-lg p-2 mb-3 focus:outline-none focus:ring-2 focus:ring-[#ffc000]"
          />
        </Autocomplete>

        <div className="rounded-lg overflow-hidden shadow-md" style={{ height: 'clamp(300px, 60vh, 500px)', marginBottom: 12 }}>
          <GoogleMap
            center={userLocation || modalMarker || mapCenter}
            zoom={12}
            mapContainerStyle={{ width: "100%", height: "100%" }}
            onClick={(e) => setModalMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
          >
            {userLocation && (
              <Marker 
                position={userLocation} 
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 20,
                  fillColor: "#4285F4",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 1,
                }}
                label="You"
              />
            )}
            {modalMarker && <Marker position={modalMarker} />}
          </GoogleMap>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setActiveModal(null)}
            className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={confirmLocation}
            className="px-4 py-2 rounded-lg bg-[#ffc000] hover:bg-[#e3ab00] font-bold"
          >
            Confirm
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default RideShareDriverRouteLocationPicker;
